import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Row, Col, Card, Statistic, Tag, Button, Table, Badge, Space,
  Typography, Progress, Tooltip, Avatar, List, Alert, Divider,
  Select, Switch, message, Spin,
} from 'antd';
import {
  FireOutlined, UserOutlined, HomeOutlined, CalendarOutlined,
  FileTextOutlined, TrophyOutlined, RobotOutlined, ArrowRightOutlined,
  ThunderboltOutlined, WhatsAppOutlined, PhoneOutlined, ReloadOutlined,
  PlusOutlined, BarChartOutlined, BellOutlined, GlobalOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const API = '/api/v1/jj';
const h = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

const TIER_CONFIG = {
  hot:  { color: '#ff4d4f', bg: '#fff1f0', label: '🔥 HOT',  icon: <FireOutlined />, priority: 0 },
  warm: { color: '#fa8c16', bg: '#fff7e6', label: '⭐ WARM', icon: <ThunderboltOutlined />, priority: 1 },
  cold: { color: '#1890ff', bg: '#e6f7ff', label: '❄️ COLD', icon: <UserOutlined />, priority: 2 },
};

const getStatusSteps = (t) => [
  { key: 'inquiry',   label: t('jj.statusInquiry','Inquiry'),   color: '#722ed1', icon: <BellOutlined /> },
  { key: 'contacted', label: t('jj.statusContacted','Contacted'), color: '#1890ff', icon: <PhoneOutlined /> },
  { key: 'viewing',   label: t('jj.statusViewing','Viewing'), color: '#fa8c16', icon: <HomeOutlined /> },
  { key: 'contract',  label: t('jj.statusContract','Contract'),   color: '#52c41a', icon: <FileTextOutlined /> },
  { key: 'done',      label: t('jj.statusDone','✅ Closed'), color: '#13c2c2', icon: <TrophyOutlined /> },
];

export default function JJDashboardPage() {
  const { t } = useTranslation();
  const STATUS_STEPS = getStatusSteps(t);
  const [analytics, setAnalytics] = useState(null);
  const [hotLeads, setHotLeads] = useState([]);
  const [followupQueue, setFollowupQueue] = useState([]);
  const [upcomingViewings, setUpcomingViewings] = useState([]);
  const [lineChannels, setLineChannels] = useState([]);
  const [lineLoading, setLineLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, hotRes, fuRes, viewRes] = await Promise.all([
        fetch(`${API}/analytics`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/customers?tier=hot&limit=10`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/followups/queue`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/viewings?upcoming=true`, { headers: h() }).then(r => r.json()),
      ]);
      setAnalytics(analyticsRes);
      setHotLeads(hotRes.customers || []);
      setFollowupQueue(fuRes.queue || []);
      setUpcomingViewings(viewRes.viewings || []);
    } catch (e) {
      message.error(t('dash.loadFailed','Load failed'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLineChannels = useCallback(async () => {
    setLineLoading(true);
    try {
      const res = await fetch(`${API}/line/channels`, { headers: h() }).then(r => r.json());
      setLineChannels(res.channels || []);
    } catch (e) {
      message.error(t('dash.lineLoadFailed','Line channel load failed'));
    } finally {
      setLineLoading(false);
    }
  }, []);

  const toggleJJMode = async (channelKey, enabled) => {
    await fetch(`${API}/line/channels/${channelKey}/jj-mode`, {
      method: 'POST', headers: h(), body: JSON.stringify({ enabled }),
    });
    message.success(`JJ Mode ${enabled ? t('dash.enabled','ON') : t('dash.disabled','OFF')} — ${channelKey.slice(0, 12)}...`);
    loadLineChannels();
  };

  useEffect(() => { loadAll(); loadLineChannels(); }, [loadAll, loadLineChannels]);

  const kpi = analytics?.kpi || {};
  const funnel = analytics?.funnel || {};
  const tiers = analytics?.tiers || {};

  const totalInPipeline = Object.values(funnel).reduce((a, b) => a + b, 0);

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            🏙️ {t('jj.dashboardTitle', 'JJ Real Estate — Bangkok AI Dashboard')}
          </Title>
          <Text type="secondary">{t('jj.dashboardSubtitle', 'Sukhumvit · Thonglor · Phromphong · Ploenchit · AI-Powered CRM')}</Text>
        </div>
        <Space>
          <Button icon={<PlusOutlined />} type="primary" onClick={() => navigate('/jj-inbox')}>
            {t('dash.newInquiry','New Inquiry')}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadAll}>{t('dash.refresh','Refresh')}</Button>
        </Space>
      </div>

      {loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', marginTop: 80 }} /> : (
        <>
          {/* KPI Cards */}
          <Row gutter={16} style={{ marginBottom: 20 }}>
            {[
              { title: t('jj.todayInquiries','Today\'s Inquiries'), value: kpi.today_inquiries, icon: <BellOutlined />, color: '#722ed1', onClick: () => navigate('/jj-inbox') },
              { title: t('jj.hotLeadsToday','🔥 Hot Leads Today'), value: kpi.hot_leads_today, icon: <FireOutlined />, color: '#ff4d4f' },
              { title: t('jj.pendingFollowups','Pending Follow-ups'), value: kpi.pending_followups, icon: <WhatsAppOutlined />, color: '#fa8c16', onClick: () => navigate('/jj-inbox?tab=followup') },
              { title: t('jj.upcomingViewings','Upcoming Viewings'), value: kpi.upcoming_viewings, icon: <CalendarOutlined />, color: '#1890ff' },
              { title: t('jj.conversionRate','Conversion Rate'), value: kpi.conversion_rate, icon: <TrophyOutlined />, color: '#52c41a', suffix: '%' },
              { title: t('jj.totalCustomers','Total Customers'), value: kpi.total_customers, icon: <UserOutlined />, color: '#13c2c2' },
            ].map((item, i) => (
              <Col span={4} key={i}>
                <Card
                  hoverable={!!item.onClick}
                  onClick={item.onClick}
                  style={{ borderTop: `3px solid ${item.color}`, cursor: item.onClick ? 'pointer' : 'default' }}
                  size="small"
                >
                  <Statistic
                    title={<Text style={{ fontSize: 12 }}>{item.title}</Text>}
                    value={item.value ?? 0}
                    suffix={item.suffix}
                    prefix={<span style={{ color: item.color }}>{item.icon}</span>}
                    valueStyle={{ color: item.color, fontSize: 24 }}
                  />
                </Card>
              </Col>
            ))}
          </Row>

          <Row gutter={16}>
            {/* Pipeline Funnel */}
            <Col span={10}>
              <Card title="🚀 Sales Pipeline" size="small" style={{ marginBottom: 16 }}>
                {STATUS_STEPS.map((step, i) => {
                  const count = funnel[step.key] || 0;
                  const pct = totalInPipeline > 0 ? Math.round(count / totalInPipeline * 100) : 0;
                  return (
                    <div key={step.key} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <Space>
                          <span style={{ color: step.color }}>{step.icon}</span>
                          <Text style={{ fontSize: 13 }}>{step.label}</Text>
                        </Space>
                        <Space>
                          <Badge count={count} style={{ backgroundColor: step.color }} />
                          <Text type="secondary" style={{ fontSize: 12 }}>{pct}%</Text>
                        </Space>
                      </div>
                      <Progress percent={pct} strokeColor={step.color} size="small" showInfo={false} />
                    </div>
                  );
                })}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  {Object.entries(tiers).map(([tier, count]) => {
                    const cfg = TIER_CONFIG[tier];
                    return (
                      <Tag key={tier} color={cfg?.color} style={{ padding: '2px 8px' }}>
                        {cfg?.label} {count}
                      </Tag>
                    );
                  })}
                </div>
              </Card>

              {/* Area Heatmap */}
              {analytics?.top_areas?.length > 0 && (
                <Card title={`📍 ${t('dash.topAreas','Top Areas')}`} size="small">
                  {analytics.top_areas.map((a, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <Text style={{ fontSize: 13 }}>
                        <GlobalOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                        {a.area}
                      </Text>
                      <Tag>{a.count} {t('dash.people','people')}</Tag>
                    </div>
                  ))}
                </Card>
              )}
            </Col>

            {/* Hot Leads */}
            <Col span={14}>
              <Card
                title={<span><FireOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />🔥 {t('dash.hotLeadsAction','Hot Leads — Action Required')}</span>}
                size="small"
                style={{ marginBottom: 16 }}
                extra={<Button size="small" onClick={() => navigate('/jj-inbox?tier=hot')}>{t('dash.viewAll','View All')}</Button>}
              >
                {hotLeads.length === 0 ? (
                  <Text type="secondary">{t('dash.noHotLeads','No Hot Leads')}</Text>
                ) : (
                  <List
                    size="small"
                    dataSource={hotLeads.slice(0, 5)}
                    renderItem={c => (
                      <List.Item
                        style={{ cursor: 'pointer', padding: '8px 4px' }}
                        onClick={() => navigate(`/jj-inbox?customer=${c.id}`)}
                        actions={[
                          <Button size="small" type="primary" danger icon={<PhoneOutlined />}>{t('dash.contact','Contact')}</Button>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<Avatar style={{ background: '#ff4d4f' }}>{(c.name || '?')[0]}</Avatar>}
                          title={
                            <Space>
                              <Text strong style={{ fontSize: 13 }}>{c.name || t('dash.unknownCustomer','Unknown')}</Text>
                              <Tag color="red" style={{ fontSize: 11 }}>{t('dash.score','Score')} {c.lead_score}</Tag>
                              <Tag style={{ fontSize: 11 }}>{c.intent === 'rent' ? t('dash.rent','🏠 Rent') : c.intent === 'buy' ? t('dash.buy','💰 Buy') : '?'}</Tag>
                            </Space>
                          }
                          description={
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {c.preferred_area || t('dash.noArea','No area')} ·
                              {t('dash.budget','Budget')} {c.budget_max ? `THB ${c.budget_max.toLocaleString()}` : t('dash.unknown','Unknown')} ·
                              {c.urgency === 'immediate' ? ` ⚡ ${t('dash.immediate','Immediate')}` : ` ${c.urgency || '?'}`}
                            </Text>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Card>

              {/* Follow-up Queue */}
              <Card
                title={<span><WhatsAppOutlined style={{ color: '#25d366' }} /> {`📨 ${t('dash.followupSchedule','Follow-up Schedule')}`}</span>}
                size="small"
                extra={<Button size="small" onClick={() => navigate('/jj-inbox?tab=followup')}>{t('dash.all','All')}</Button>}
              >
                {followupQueue.length === 0 ? (
                  <Alert message={t('dash.noFollowups','No follow-up tasks today 🎉')} type="success" showIcon style={{ padding: '4px 10px' }} />
                ) : (
                  followupQueue.slice(0, 4).map(fu => (
                    <div key={fu.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <Space>
                            <Tag color={fu.stage === 'day3' ? 'blue' : fu.stage === 'day7' ? 'orange' : 'red'}>
                              {fu.stage}
                            </Tag>
                            <Text strong style={{ fontSize: 13 }}>{fu.customer?.name || t('dash.unknown','Unknown')}</Text>
                            <Tag color={TIER_CONFIG[fu.customer?.tier]?.color}>{fu.customer?.tier?.toUpperCase()}</Tag>
                          </Space>
                          <div style={{ marginTop: 4, fontSize: 12, color: '#666', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {fu.generated_message}
                          </div>
                        </div>
                        <Space>
                          <Button size="small" icon={<WhatsAppOutlined />} style={{ color: '#25d366', borderColor: '#25d366' }}>
                            {t('dash.send','Send')}
                          </Button>
                        </Space>
                      </div>
                    </div>
                  ))
                )}
              </Card>
            </Col>
          </Row>

          {/* Upcoming Viewings */}
          {upcomingViewings.length > 0 && (
            <Card title={`🏠 ${t('dash.upcomingViewings','Upcoming Viewings')}`} size="small" style={{ marginTop: 16 }}>
              <Table
                size="small"
                dataSource={upcomingViewings}
                rowKey="id"
                pagination={false}
                columns={[
                  { title: t('dash.customer','Customer'), dataIndex: ['customer', 'name'], render: v => <Text strong>{v || '?'}</Text>, width: 120 },
                  { title: 'Tier', dataIndex: ['customer', 'tier'], render: v => <Tag color={TIER_CONFIG[v]?.color}>{v?.toUpperCase()}</Tag>, width: 80 },
                  { title: t('dash.property','Property'), dataIndex: 'listing_title', ellipsis: true },
                  { title: t('dash.time','Time'), dataIndex: 'scheduled_at', render: v => v ? new Date(v).toLocaleString('zh-TW') : 'TBD', width: 160 },
                  {
                    title: '', width: 160,
                    render: (_, r) => (
                      <Space>
                        <Button size="small" style={{ color: '#52c41a', borderColor: '#52c41a' }}>{t('dash.closed','✅ Closed')}</Button>
                        <Button size="small" danger>{t('dash.lost','❌ Not Closed')}</Button>
                      </Space>
                    ),
                  },
                ]}
              />
            </Card>
          )}

          {/* Line Channel JJ Mode Management */}
          <Card
            title={<span>{`📱 ${t('dash.lineChannelTitle','LINE Channel JJ Auto-Intake')}`}</span>}
            size="small"
            style={{ marginTop: 16 }}
            extra={
              <Space>
                <Badge count={lineChannels.filter(c => c.jj_mode).length} style={{ backgroundColor: '#52c41a' }}>
                  <Text style={{ fontSize: 12 }}>{t('dash.enabled','Enabled')}</Text>
                </Badge>
                <Button size="small" icon={<ReloadOutlined />} onClick={loadLineChannels} loading={lineLoading} />
              </Space>
            }
          >
            <Alert
              message="{t('dash.lineChannelDesc','When JJ Mode is ON, messages to this LINE channel automatically enter JJ CRM for AI Lead scoring.')}"
              type="info" showIcon style={{ marginBottom: 12, padding: '4px 10px', fontSize: 12 }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, maxHeight: 280, overflow: 'auto' }}>
              {lineChannels.map(ch => (
                <div
                  key={ch.channel_key}
                  style={{
                    padding: '8px 10px', borderRadius: 8,
                    background: ch.jj_mode ? '#f6ffed' : '#fafafa',
                    border: `1px solid ${ch.jj_mode ? '#b7eb8f' : '#e8e8e8'}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                >
                  <div>
                    <Text strong style={{ fontSize: 12 }}>{ch.name}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 10 }}>bot: {ch.bot_name || '-'}</Text>
                  </div>
                  <Switch
                    size="small"
                    checked={ch.jj_mode}
                    checkedChildren="JJ"
                    unCheckedChildren="off"
                    onChange={(v) => toggleJJMode(ch.channel_key, v)}
                  />
                </div>
              ))}
            </div>
          </Card>

          {/* MarCom Integration Panel */}
          <Card title={`🔗 ${t('dash.marcomTitle','MarCom AI Integration')}`} size="small" style={{ marginTop: 16 }}>
            <Row gutter={12}>
              {[
                { label: t('dash.fbAds','FB Ads'), icon: '📢', desc: t('dash.fbAdsDesc','Auto-create FB ads'), path: '/fb-ad-creator', color: '#1877f2' },
                { label: t('dash.aiBot','AI Sales Bot'), icon: '🤖', desc: t('dash.aiBotDesc','AI auto-replies'), path: '/ai-sales', color: '#722ed1' },
                { label: t('dash.emailSeq','Email Follow-up'), icon: '📧', desc: t('dash.emailSeqDesc','Auto email sequence'), path: '/email-compose', color: '#52c41a' },
                { label: t('dash.propSearch','Property Search'), icon: '🏠', desc: t('dash.propSearchDesc','HouseAI 670+ properties'), path: '/house-ai-rental', color: '#fa8c16' },
                { label: t('dash.aiPresent','AI Presentation'), icon: '📊', desc: t('dash.aiPresentDesc','Auto-generate presentation'), path: '/presentation', color: '#13c2c2' },
                { label: t('dash.marketSchedule','Marketing'), icon: '📅', desc: t('dash.marketScheduleDesc','Auto-post to DDproperty/FB'), path: '/marketing-scheduler', color: '#eb2f96' },
              ].map((item, i) => (
                <Col span={4} key={i}>
                  <Card
                    hoverable size="small"
                    onClick={() => navigate(item.path)}
                    style={{ textAlign: 'center', cursor: 'pointer', borderTop: `3px solid ${item.color}` }}
                  >
                    <div style={{ fontSize: 24, marginBottom: 4 }}>{item.icon}</div>
                    <Text strong style={{ fontSize: 12, display: 'block' }}>{item.label}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>{item.desc}</Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </>
      )}
    </div>
  );
}
